/**
 * The screen itself: background, padding, gap, alignment (DESIGN §4.3).
 *
 * Shown in the inspector when no block is selected, because "the screen" is
 * what you are editing when you are not editing anything on it. The panel's
 * header says so; this is only the body of it.
 */

import type { ScreenDesignAsset } from "../api/types";
import { useI18n } from "../i18n";
import AssetPicker from "./AssetPicker";
import { ColorField, Group, NumberField, SelectField, ToggleField } from "./fields";
import {
  DEFAULTS,
  GRADIENT_ANGLES,
  RANGES,
  VERTICAL_ALIGNS,
  type Background,
  type Theme,
} from "./spec";

const BACKGROUND_TYPES = ["solid", "gradient", "image"] as const;

export default function ThemePanel({
  theme,
  assets,
  uploading,
  uploadError,
  onChange,
  onUpload,
  onDeleteAsset,
}: {
  theme: Theme;
  assets: ScreenDesignAsset[];
  uploading: boolean;
  uploadError?: unknown;
  onChange: (theme: Theme) => void;
  onUpload: (file: File) => void;
  onDeleteAsset: (assetId: string) => void;
}) {
  const { t } = useI18n();
  const background = theme.background ?? DEFAULTS.theme.background;
  const patch = (next: Partial<Theme>) => onChange({ ...theme, ...next });

  /** Switching type keeps whatever the new one needs and drops the rest. */
  const switchBackground = (type: (typeof BACKGROUND_TYPES)[number]) => {
    if (type === background.type) return;
    const next: Background =
      type === "solid"
        ? { type, color: background.type === "gradient" ? background.colors[0] : "#000000" }
        : type === "gradient"
          ? {
              type,
              colors: [background.type === "solid" ? background.color : "#0B1E3A", "#123B7A"],
              angle: 180,
            }
          : { type, asset: "", scale: "cover" };
    patch({ background: next });
  };

  return (
    <div className="design-inspector-body">
      <Group label={t("design.group.background")}>
        <SelectField
          label={t("design.background")}
          value={background.type}
          options={BACKGROUND_TYPES}
          labelFor={(option) => t(`design.background.${option}`)}
          onChange={switchBackground}
        />

        {background.type === "solid" && (
          <ColorField
            label={t("design.color")}
            value={background.color}
            onChange={(color) => patch({ background: { type: "solid", color } })}
          />
        )}

        {background.type === "gradient" && (
          <>
            {background.colors.map((color, index) => (
              <ColorField
                key={index}
                label={t("design.gradientStop", { n: index + 1 })}
                value={color}
                onChange={(value) => {
                  const colors = [...background.colors];
                  colors[index] = value;
                  patch({ background: { ...background, colors } });
                }}
              />
            ))}
            <div className="row design-stop-actions">
              <button
                type="button"
                disabled={background.colors.length >= 3}
                onClick={() =>
                  patch({
                    background: { ...background, colors: [...background.colors, "#FFFFFF"] },
                  })
                }
              >
                {t("design.addStop")}
              </button>
              <button
                type="button"
                disabled={background.colors.length <= 2}
                onClick={() =>
                  patch({ background: { ...background, colors: background.colors.slice(0, -1) } })
                }
              >
                {t("design.removeStop")}
              </button>
            </div>
            <SelectField
              label={t("design.angle")}
              value={String(background.angle ?? 180)}
              options={GRADIENT_ANGLES.map(String)}
              labelFor={(option) => `${option}°`}
              onChange={(angle) => patch({ background: { ...background, angle: Number(angle) } })}
            />
          </>
        )}

        {background.type === "image" && (
          <>
            <AssetPicker
              assets={assets}
              value={background.asset || null}
              uploading={uploading}
              error={uploadError}
              onPick={(asset) => patch({ background: { ...background, asset } })}
              onUpload={onUpload}
              onDelete={onDeleteAsset}
            />
            <SelectField
              label={t("design.scale")}
              value={background.scale ?? "cover"}
              options={["cover", "contain"] as const}
              labelFor={(option) => t(`design.scale.${option}`)}
              onChange={(scale) => patch({ background: { ...background, scale } })}
            />
            <ToggleField
              label={t("design.overlay")}
              value={background.overlay !== undefined}
              onChange={(on) =>
                patch({
                  background: on
                    ? { ...background, overlay: "#00000099" }
                    : { type: "image", asset: background.asset, scale: background.scale },
                })
              }
            />
            {background.overlay !== undefined && (
              <ColorField
                label={t("design.overlayColor")}
                value={background.overlay}
                onChange={(overlay) => patch({ background: { ...background, overlay } })}
              />
            )}
          </>
        )}
      </Group>

      <Group label={t("design.group.layout")}>
        <NumberField
          label={t("design.padding")}
          unit="dp"
          value={theme.padding ?? DEFAULTS.theme.padding}
          range={RANGES.theme.padding}
          onChange={(padding) => patch({ padding })}
        />
        <NumberField
          label={t("design.gap")}
          unit="dp"
          value={theme.gap ?? DEFAULTS.theme.gap}
          range={RANGES.theme.gap}
          onChange={(gap) => patch({ gap })}
        />
        <SelectField
          label={t("design.verticalAlign")}
          value={theme.verticalAlign ?? DEFAULTS.theme.verticalAlign}
          options={VERTICAL_ALIGNS}
          labelFor={(option) => t(`design.verticalAlign.${option}`)}
          onChange={(verticalAlign) => patch({ verticalAlign })}
        />
        <SelectField
          label={t("design.statusBarIcons")}
          value={theme.statusBarIcons ?? DEFAULTS.theme.statusBarIcons}
          options={["light", "dark"] as const}
          labelFor={(option) => t(`design.statusBarIcons.${option}`)}
          onChange={(statusBarIcons) => patch({ statusBarIcons })}
        />
      </Group>

      <p className="muted small design-hint">{t("design.nameIconNote")}</p>
    </div>
  );
}
